import { useEffect, useRef, useState } from "react";
import { FieldError, useForm } from "react-hook-form";
import { postApplication } from "../../API/Applications/applicationService";
import { fetchAllJobs } from "../../API/recruitment/recruitmentService";
import icon from "../../images/icons/Design sans titre (44).png";
import { showDate } from "../../lib/dateFormater";
import Loader from "../Loader/Loader";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardTitle } from "../ui/card";
import { LoadingOverlay } from "../ui/loading-overlay";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface Job {
  id: number;
  title: string;
  createdAt: string;
  reference: string;
}

interface DataState {
  totalPages: number;
  totalCount: number;
  jobs: Job[];
}

interface NestedFieldError extends FieldError {
  file?: FieldError;
}
const HiringHeroSection = () => {
  const formSchema = z.object({
    reference: z.string().optional(),
    lastName: z
      .string({ required_error: "Ce champ est obligatoire" })
      .min(1, { message: "Ce champ est obligatoire" }),
    firstName: z
      .string({ required_error: "Ce champ est obligatoire" })
      .min(1, { message: "Ce champ est obligatoire" }),
    email: z
      .string({ required_error: "Ce champ est obligatoire" })
      .email({ message: "Veuillez renseigner un mail valide" }),
    phone: z
      .string({ required_error: "Ce champ est obligatoire" })
      .min(1, { message: "Ce champ est obligatoire" }),
    coverLetter: z.object({
      key: z.string(),
      fileName: z.string(),
      name: z.string(),
      fileSize: z.number(),
      size: z.number(),
      file: z.instanceof(File, { message: "Ce champ est obligatoire" }),
    }),
    resume: z.object({
      key: z.string(),
      fileName: z.string(),
      name: z.string(),
      fileSize: z.number(),
      size: z.number(),
      file: z.instanceof(File, { message: "Ce champ est obligatoire" }),
    }),
  });

  type FormData = {
    reference: string;
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
    coverLetter: { [key: string]: any } | null;
    resume: { [key: string]: any } | null;
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema), // Replace custom resolver
  });

  const { setValue, handleSubmit, reset, control, formState } = form;

  const formRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<DataState>({
    totalPages: 0,
    totalCount: 0,
    jobs: [],
  });

  const fetchData = async () => {
    setLoading(true);
    const response = await fetchAllJobs(currentPage);
    if (response) {
      const { data, totalPages, totalCount } = response;
      setData({
        ...data,
        jobs: data,
        totalPages,
        totalCount,
      });
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const handlePostuler = (reference: string, event: React.MouseEvent) => {
    event.preventDefault();
    setValue("reference", reference);

    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 100);
  };

  const onSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      await postApplication(data);
      // Reset all form fields
      reset({
        reference: "",
        lastName: "",
        firstName: "",
        email: "",
        phone: "",
        coverLetter: undefined,
        resume: undefined,
      });
      // Reset file inputs manually
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input: any) => {
        input.value = '';
      });
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <LoadingOverlay
        isVisible={submitting}
        message="Envoi de votre candidature..."
        subMessage="Veuillez patienter"
      />
      <div className="container mx-auto mt-20 mb-20 md:px-72">
        <h1 className="text-2xl font-bold text-black">Nos Offres d'emploi</h1>

        <div>
          {loading ? (
            <div className="flex justify-center">
              <Loader />
            </div>
          ) : data.totalCount === 0 ? (
            <div className="flex mt-2 space-x-2">
              <div>
                <img src={icon} alt="" />
              </div>
              <div>
                Nous n'avons pas d'offres d'emploi actuellement, mais n'hésitez
                pas à revenir plus tard ou à nous suivre pour être informé des
                prochaines opportunités
              </div>
            </div>
          ) : (
            <div className="mt-10">
              {data.jobs.map((item) => (
                <Card
                  className="p-5 mt-5 border-2 border-primary"
                  key={item?.id}
                >
                  <CardTitle>
                    <div className="flex justify-end">
                      <Badge className="text-end">{item?.reference}</Badge>
                    </div>

                    <div className="flex justify-start ml-5 ">
                      <div className="flex flex-col items-start justify-center ">
                        <div>
                          <h1 className="text-start">{item?.title}</h1>
                        </div>
                        <h1 className="text-xs text-start text-neutral-400">
                          Publier le : {showDate(item?.createdAt)}
                        </h1>
                      </div>
                    </div>
                  </CardTitle>
                  <CardContent>
                    <p className="mt-3">
                      Customization is very easy with this theme. Clean and
                      quality design and full support for any kind of request!
                      Great theme!
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button
                      type="button"
                      onClick={(e) => handlePostuler(item?.reference, e)}
                    >
                      Postuler
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>

        {!loading && data.totalPages > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className={`px-4 py-2 rounded ${
                currentPage === 1
                  ? "bg-gray-300"
                  : "bg-primary text-white hover:bg-primary-dark"
              }`}
            >
              Précédent
            </button>
            <span className="px-4 py-2 border rounded">
              {currentPage} / {data.totalPages}
            </span>
            <button
              disabled={currentPage === data.totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, data.totalPages))
              }
              className={`px-4 py-2 rounded ${
                currentPage === data.totalPages
                  ? "bg-gray-300"
                  : "bg-primary text-white hover:bg-primary-dark"
              }`}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
      <div className="">
        {" "}
        <div className="bg-aquaFoncé pb-32">
          <h1 className="p-5 text-3xl text-center text-white">
            Soumettez votre candidature en ligne pour un stage, une alternance
            ou une demande d'emploi.
          </h1>
        </div>
      </div>

      <div
        className="relative pb-1 mt-10 mb-10 lg:mb-40 lg:mt-96 "
        ref={formRef}
      >
        <div className="left-0 right-0 w-full ml-auto mr-auto none lg:absolute top-[-430px]">
          <div className="flex justify-center ">
            <Card>
              <CardContent className="p-0 pt-0">
                <div className="grid gap-5 p-5 grid-rows md:grid-cols">
                  <Form {...form}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {form.watch("reference") && (
                        <FormField
                          name="reference"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Référence</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Référence"
                                  readOnly={true}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <div className="flex flex-col lg:space-x-4 lg:flex-row">
                        <FormField
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Nom</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Nom"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Prénom</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Prénom"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <FormField
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <Label>Email</Label>

                              <FormControl>
                                <Input
                                  className="mt-5 border border-primary "
                                  placeholder="Email"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <div className="flex flex-col justify-between mt-1 lg:space-x-4 lg:flex-row">
                          <FormField
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <Label>Téléphone</Label>

                                <FormControl>
                                  <Input
                                    className="mt-5 border border-primary md:w-[440px]"
                                    placeholder="Téléphone"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col mt-2 lg:space-x-4 lg:flex-row">
                        <FormField
                          name="coverLetter"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Label>Lettre de motivation *</Label>
                              <FormControl>
                                <Input
                                  name="coverLetter"
                                  type="file"
                                  accept="application/pdf"
                                  className="mt-5 border border-primary md:w-[440px]"
                                  placeholder="Nom"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const mappedFile = {
                                        key: file.name,
                                        fileName: file.name,
                                        name: file.name,
                                        fileSize: file.size,
                                        size: file.size,
                                        file: file,
                                      };
                                      form.setValue("coverLetter", mappedFile, {
                                        shouldValidate: true,
                                      });
                                    }
                                  }}
                                />
                              </FormControl>

                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="resume"
                          render={({ field, fieldState }) => {
                            return (
                              <FormItem>
                                <Label>CV *</Label>
                                <FormControl>
                                  <Input
                                    type="file"
                                    accept="application/pdf"
                                    className="mt-5 border border-primary md:w-[440px]"
                                    placeholder="Prénom"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const mappedFile = {
                                          key: file.name,
                                          fileName: file.name,
                                          name: file.name,
                                          fileSize: file.size,
                                          size: file.size,
                                          file: file,
                                        };
                                        form.setValue("resume", mappedFile, {
                                          shouldValidate: true,
                                        });
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <div className="flex mt-5 space-x-4">
                        <div>
                          <Button
                            className="w-full rounded-none lg:w-56"
                            type="submit"
                            disabled={submitting}
                            isLoading={submitting}
                          >
                            {submitting ? 'Envoi en cours...' : 'Envoyer'}
                          </Button>
                        </div>
                        <div>
                          <Button
                            className="w-full rounded-none lg:w-56"
                            type="button"
                            disabled={submitting}
                            onClick={() => {
                              reset({
                                reference: "",
                                lastName: "",
                                firstName: "",
                                email: "",
                                phone: "",
                                coverLetter: undefined,
                                resume: undefined,
                              });
                              const fileInputs = document.querySelectorAll('input[type="file"]');
                              fileInputs.forEach((input: any) => {
                                input.value = '';
                              });
                            }}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringHeroSection;
